import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import type { U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { useU2202Context } from "../U2202Context";
import { NS } from "../U2202";
import { doSearch, luHandleAppiError, type U2202ConnectedUser } from "../U2202Service";
import { U2202ConnectedUserListToolBar } from "./list/U2202ConnectedUserListToolBar";

/* eslint-disable react-refresh/only-export-components */
export function useU2202ConnectedUserListHandlerActions<T>(handlers: U2000TableHandlers<T>): U2000TableHandlers<T> {
  return handlers;
}

// Function to get columns definition
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "userId",
      label: t("lblUserId", { ns: NS }),
      widthClass: "w-[280px]",
      type: "string",
      align: "left",
      inGridField: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      sortable: true,
      isCriteriaField: false,
    },
    {
      name: "username",
      label: t("lblUsername", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: true,
    },
    {
      name: "connectionCount",
      label: t("lblConnectionCount", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "center",
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
    },
    {
      name: "firstName",
      label: t("lblFirstName", { ns: NS }),
      widthClass: "w-[150px]",
      type: "string",
      align: "left",
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
    },
    {
      name: "lastName",
      label: t("lblLastName", { ns: NS }),
      widthClass: "w-[150px]",
      type: "string",
      align: "left",
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
    },
  ];
}

const U2202ConnectedUserList: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { initLU, searchStateHandlers, gridStateHandlers } = useU2202Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);

  // Initialize columns on mount or when 't' changes
  useEffect(() => {
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
  }, [t]);

  // Call Search Handlers for Connected User
  const handleSearch = useU2000SearchHandlers<U2202ConnectedUser>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U2202ConnectedUser>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
      void consumeQsSessionRef.current();
    },
    onError: ({ error }: { error: unknown }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- aligné sur U2101 (error API)
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Manage Handler Actions
  const u2202ConnectedUserListHandlerActions: U2000TableHandlers<U2202ConnectedUser> = useU2202ConnectedUserListHandlerActions<U2202ConnectedUser>({
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: () => {},
    onAdd: () => {},
    onModify: () => {},
    onDelete: () => {},
    onExportExcel: async () => {},
  });

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U2202",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  // Recherche initiale lorsque launchSearch est true (sans critère par défaut)
  const initialSearchDone = useRef(false);
  useEffect(() => {
    if (!initLU?.launchSearch || searchStateHandlers.getColumns().length === 0 || initialSearchDone.current) {
      return;
    }
    initialSearchDone.current = true;

    const currentFields = searchStateHandlers.getFields();
    if (currentFields.length === 0) {
      searchStateHandlers.setFields(
        searchStateHandlers
          .getColumns()
          .filter((col: U2000TableColumn) => col.isDefaultColumn)
          .map((col: U2000TableColumn) => ({ field: { ...col } })),
      );
    }

    const currentSortedFields = searchStateHandlers.getSortedFields();
    if (currentSortedFields.length === 0) {
      searchStateHandlers.setSortedFields(
        searchStateHandlers
          .getColumns()
          .filter((col: U2000TableColumn) => col.isDefaultSortedColumn)
          .map((col: U2000TableColumn) => ({ field: { ...col }, sortOrder: "ASC" })),
      );
    }

    requestAnimationFrame(() => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(1), U2000GridStateEnumPaginationDirection.NEXTPAGE);
    });
  }, [initLU?.launchSearch, searchStateHandlers, gridStateHandlers, handleSearch]);

  // S'assurer que les colonnes sont chargées avant le rendu
  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  const onRefresh = () => {
    handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(1), U2000GridStateEnumPaginationDirection.NEXTPAGE);
  };

  // Render
  return (
    <U2000BasicTable<U2202ConnectedUser>
      searchStateHandlers={searchStateHandlers}
      gridStateHandlers={gridStateHandlers}
      label={t("lblConnectedUser", { count: gridStateHandlers.getTotalRows(), ns: NS })}
      actionsButtons={<U2202ConnectedUserListToolBar gridStateHandlers={gridStateHandlers} handlerActions={u2202ConnectedUserListHandlerActions} onRefresh={onRefresh} />}
      handlerActions={u2202ConnectedUserListHandlerActions}
      allowRowSelection={false}
      hideSearchPanel={initLU.hideSearch}
    />
  );
};

export default U2202ConnectedUserList;
